import {combine, guard, sample} from 'effector';

import {RestError, showRestError} from 'core/errors';
import {initFiltersTable, initSortingsTable} from 'core/hooks/grid2.0';
import {profile$} from 'models/auth';
import {mapFilters, mapSortings} from 'utils/grid-mappers';

import {OrdersCreateGate} from '../../orders/create';

import {billingAccess$} from './access';

import {
    balance$,
    transactionsBalance$,
    loadTransactionsBalanceFx,
    BillingGate,
    billingList$,
    defaultFilters,
    defaultSortings,
    filters$,
    filtersChanged,
    filtersReset,
    loadBalanceDataFx,
    loadBillingDataFx,
    pageNumber$,
    pageNumberChanged,
    pageSize$,
    pageSizeChanged,
    sortings$,
    sortingsChanged,
    sortingsReset,
    stateReset,
    tableStateChanged,
    updateBalance,
} from './index';

billingList$
    .on(loadBillingDataFx.done, (current, {params, result}) => {
        const access = billingAccess$.getState();
        const allowed = params.allOrganizations
            ? access.canViewAllTransactions
            : access.canViewOwnTransactions &&
              params.legalIdentityId === profile$.getState()?.client?.legalIdentityId;
        return BillingGate.status.getState() && allowed ? result : current;
    })
    .reset([BillingGate.close, profile$]);
balance$.on(loadBalanceDataFx.done, (_, {result}) => result).reset(profile$);
transactionsBalance$
    .on(loadTransactionsBalanceFx.done, (current, {params, result}) => {
        const access = billingAccess$.getState();
        const allowed = params.allOrganizations
            ? access.canViewAllTransactions
            : access.canViewOwnTransactions &&
              params.legalIdentityId === profile$.getState()?.client?.legalIdentityId;
        return BillingGate.status.getState() && allowed ? result : current;
    })
    .reset([BillingGate.close, profile$]);

initFiltersTable(filters$, filtersChanged, filtersReset, defaultFilters, stateReset);
initSortingsTable(sortings$, sortingsChanged, sortingsReset, defaultSortings, stateReset);

const identity$ = profile$.map((user) => user?.client?.legalIdentityId);
const context$ = combine({
    access: billingAccess$,
    legalIdentityId: identity$,
    active: BillingGate.status,
});

sample({
    clock: guard({
        clock: [
            BillingGate.open,
            pageNumberChanged,
            pageSizeChanged,
            tableStateChanged,
            profile$.updates,
        ],
        source: combine({
            context: context$,
            sortings: sortings$,
            pageNumber: pageNumber$,
            pageSize: pageSize$,
            filters: filters$,
        }),
        filter: ({context}) =>
            context.active &&
            context.access.canViewTransactions &&
            (context.access.canViewAllTransactions || !!context.legalIdentityId),
    }),
    fn: ({pageNumber, pageSize, filters, sortings, context}) => ({
        paging: {pageNumber, pageSize},
        filters: mapFilters(filters),
        sortings: mapSortings(sortings),
        legalIdentityId: context.legalIdentityId,
        allOrganizations: context.access.canViewAllTransactions,
    }),
    target: loadBillingDataFx,
});

sample({
    clock: guard({
        clock: [OrdersCreateGate.open, updateBalance],
        source: combine({id: identity$, access: billingAccess$}),
        filter: ({id, access}) => !!id && access.canReadOwnBalance,
    }),
    fn: ({id}) => id,
    target: loadBalanceDataFx,
});

sample({
    clock: guard({
        clock: [BillingGate.open, updateBalance, profile$.updates],
        source: context$,
        filter: ({access, legalIdentityId, active}) =>
            active &&
            access.canViewTransactions &&
            (access.canViewAllTransactions || !!legalIdentityId),
    }),
    fn: ({access, legalIdentityId}) => ({
        allOrganizations: access.canViewAllTransactions,
        legalIdentityId,
    }),
    target: loadTransactionsBalanceFx,
});

loadBillingDataFx.failData.watch((err) => showRestError(err as RestError));
loadTransactionsBalanceFx.failData.watch((err) => showRestError(err as RestError));
